import { isCalculationTacticResultUnavailable } from './actions';
import { Context } from '@/overmind';
import { CalculationState } from './createCalculationState';
import { Feature } from '@/overmind/effects/adapters/ArcgisMapAdapter/mapToInternalFeature';
import { CompletedCalculationTactic, Container } from '.';

/*
This is a duplicate of the specification in the EUNF: https://github.com/Volue/pg-anb-network-service/blob/main/AnB.NetworkService/AnB.NetworkService.Adapters.ArcGis.Services/UtilityNetworkObjects/ElectricNetworkAssembly.cs
There should be a single place for this kind of specification and also for logic that determines
the type of a grid element.
Grid Planner Web App also has the same specification: https://github.com/Volue/pg-anb-grid-planner-web/blob/48421bfcf9aadfb412334a816e1d3bb613683e18/src/shared/lib/gis/application/trace-runner/assembly-specification.ts#L41

This might be a shared capability, related to a grid assets, but based on the current low team capacity and no
plans for building shared capabilities, we will keep this specification here for now.
*/
const EUNF_assemblySpecification = [
  { type: "hvSubstation", assetGroup: 202, assetType: 485 },
  { type: "mvSubstation", assetGroup: 202, assetType: 486 },
  { type: "lvCabinet", assetGroup: 203, assetType: 497 },
  { type: "lvTapOff", assetGroup: 5, assetType: 124 },
  { type: "lvBay", assetGroup: 32, assetType: 93 },
  { type: "mvBay", assetGroup: 33, assetType: 93 }
];
export function initiatingGridElement(state: CalculationState, rootState: Context["state"]): CalculationState["initiatingGridElement"] | undefined {
  if (state.calculationContext.type === 'NOT_AVAILABLE') {
    return undefined;
  }
  if (isCalculationTacticResultUnavailable(state.calculationTactic.value)) {
    return undefined;
  }
  if (rootState.arcgis.featureGeometries.current !== 'LOADED') {
    return undefined;
  }
  const initiatingObjectId = state.calculationContext.calculationContext.relatedObjectId;
  const initiatingFeature = rootState.arcgis.featureGeometries.value.flatMap(collection => collection.features).find(feature => feature.gc.globalId === initiatingObjectId);

  if (!initiatingFeature) {
    return undefined;
  }
  
  const isAssembly = 
    EUNF_assemblySpecification
    .some(spec => spec.assetGroup === Feature.attributeOf(initiatingFeature, "assetgroup") && spec.assetType === Feature.attributeOf(initiatingFeature, "assettype"));
  const assemblyAsContainer = 
    state.calculationTactic.value.calculationSetResult.grid.containment
    .find(container => container.containerId === initiatingObjectId)
  const containerForAssembly = 
    state.calculationTactic.value.calculationSetResult.grid.containment
    .find(container => container.containedObjects.find(containedObject => containedObject.objectId === initiatingObjectId) && isAssembly)

  if(isAssembly) {
    if(assemblyAsContainer && assemblyAsContainer.containerType === "Substation") {
      const anyContainedPowerTransformerGlobalId = findAnyContainedPowerTransformer(state.calculationTactic.value, assemblyAsContainer);
      
      if (anyContainedPowerTransformerGlobalId.length > 0) {
        return {
          globalId: anyContainedPowerTransformerGlobalId[0].objectId,
          type: "NetworkAssembly"
        };
      }
    } else if (containerForAssembly && containerForAssembly.containerType === "Substation") {
      const anyContainedPowerTransformerGlobalId = findAnyContainedPowerTransformer(state.calculationTactic.value, containerForAssembly)
      
      if (anyContainedPowerTransformerGlobalId.length > 0) {
        return {
          globalId: anyContainedPowerTransformerGlobalId[0].objectId,
          type: "NetworkAssembly"
        };
      }
    } else if(assemblyAsContainer && assemblyAsContainer.containerType === "Cabinet") {
      const anyContainedBusbarGlobalId = findAnyContainedBusbarId(state.calculationTactic.value, assemblyAsContainer)
      
      if (anyContainedBusbarGlobalId.length > 0) {
        return {
          globalId: anyContainedBusbarGlobalId[0].objectId,
          type: "NetworkAssembly"
        };
      }
    } else if (containerForAssembly && containerForAssembly.containerType === "Cabinet") {
      const anyContainedBusbarGlobalId = findAnyContainedBusbarId(state.calculationTactic.value, containerForAssembly);
      
      if (anyContainedBusbarGlobalId.length > 0) {
        return {
          globalId: anyContainedBusbarGlobalId[0].objectId,
          type: "NetworkAssembly"
        };
      }
    }
  }
  return {
    globalId: state.calculationContext.calculationContext.relatedObjectId,
    type: "Other"
  };
}
function findAnyContainedPowerTransformer(calculationTactic: CompletedCalculationTactic, containerForAssembly: Container) {
  return calculationTactic.calculationSetResult.grid.functional.powerTransformers
    .filter(trafo => containerForAssembly.containedObjects.find(gridElement => gridElement.objectId === trafo.objectId));
}

function findAnyContainedBusbarId(calculationTactic: CompletedCalculationTactic, assemblyAsContainer: Container) {
  return calculationTactic.calculationSetResult.grid.functional.busbars
    .filter(busbar => assemblyAsContainer.containedObjects.find(gridElement => gridElement.objectId === busbar.objectId));
}

